import { FormEvent } from "react"
import { useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { ProgramSelect, type ProgramOption } from "@/components/ProgramSelect"

type ExamFields = {
  title: string
  exam_version_id: string
  instructions: string
  time_limit_minutes: string
  program_id: string
}

export function ExamFormDialog({
  open,
  onOpenChange,
  action,
  method,
  title,
  submitLabel,
  examVersions,
  programs = [],
  initial,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  action: string
  method: "post" | "patch"
  title: string
  submitLabel: string
  examVersions: { id: number; name: string }[]
  programs?: ProgramOption[]
  initial?: Partial<ExamFields>
}) {
  const form = useForm<ExamFields>({
    title: initial?.title ?? "",
    exam_version_id: initial?.exam_version_id ?? String(examVersions[0]?.id ?? ""),
    instructions:
      initial?.instructions ??
      "Read each case carefully. Answer the questions that follow each part of the case. You can flag questions and change answers until you submit each half.",
    time_limit_minutes: initial?.time_limit_minutes ?? "225",
    program_id: String(programs[0]?.id ?? ""),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.submit(method, action, {
      preserveScroll: true,
      onSuccess: () => {
        onOpenChange(false)
        if (method === "post") form.reset()
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <label htmlFor="exam-title">Title</label>
              <Input
                id="exam-title"
                required
                autoFocus
                aria-invalid={!!form.errors.title}
                value={form.data.title}
                onChange={(e) => form.setData("title", e.target.value)}
              />
              {form.errors.title && <p className="text-xs text-danger-display">{form.errors.title}</p>}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="exam-version">Exam version</label>
                <Select
                  id="exam-version"
                  value={form.data.exam_version_id}
                  onChange={(e) => form.setData("exam_version_id", e.target.value)}
                >
                  {examVersions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <label htmlFor="exam-time">Time limit (minutes)</label>
                <Input
                  id="exam-time"
                  type="number"
                  min={1}
                  max={600}
                  required
                  value={form.data.time_limit_minutes}
                  onChange={(e) => form.setData("time_limit_minutes", e.target.value)}
                />
              </div>
            </div>
            <ProgramSelect
              id="exam-program"
              programs={programs}
              value={form.data.program_id}
              onChange={(value) => form.setData("program_id", value)}
              error={(form.errors as Record<string, string>).program}
            />
            <div className="space-y-2">
              <label htmlFor="exam-instructions">Instructions shown before the exam</label>
              <textarea
                id="exam-instructions"
                className="form-control form-control-textarea"
                value={form.data.instructions}
                onChange={(e) => form.setData("instructions", e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={form.processing}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
